import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown, ChevronRight, Circle, CircleDot, Loader2, Minus } from 'lucide-react';
import {
    ServiceStatusBadge,
    Wake,
    WakePlan,
    WakeStep,
    apiErrorMessage,
    formatCredits,
    formatDuration,
    formatRelativeTime,
    wakeReasonLabel,
} from '../services/servicesShared';
import { formatDate } from '../utils/dateFormatter';

const ACTIVE_WAKE_STATUSES = new Set(['pending', 'queued', 'running']);

export const WakePlanList: React.FC<{ plan: WakePlan | null | undefined }> = ({ plan }) => {
    if (!plan?.items?.length) return null;
    return (
        <ol className="space-y-1">
            {plan.items.map((item) => (
                <li key={item.id} className="flex items-start gap-2 text-sm">
                    {item.status === 'done' ? (
                        <Check className="h-4 w-4 mt-0.5 text-green-600 shrink-0" />
                    ) : item.status === 'in_progress' ? (
                        <CircleDot className="h-4 w-4 mt-0.5 text-blue-600 animate-pulse shrink-0" />
                    ) : item.status === 'skipped' ? (
                        <Minus className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                    ) : (
                        <Circle className="h-4 w-4 mt-0.5 text-muted-foreground/60 shrink-0" />
                    )}
                    <span className={item.status === 'skipped' ? 'text-muted-foreground line-through' : ''}>
                        {item.title}
                        {item.note ? <span className="text-xs text-muted-foreground"> — {item.note}</span> : null}
                    </span>
                </li>
            ))}
        </ol>
    );
};

const StepRow: React.FC<{ step: WakeStep; index: number }> = ({ step, index }) => {
    const [open, setOpen] = useState(false);
    const label =
        step.kind === 'model'
            ? 'Model call'
            : step.kind === 'thought'
                ? 'Thought'
                : step.kind === 'result'
                    ? 'Result'
                    : step.tool || 'Tool';
    const expandable = Boolean(step.args || step.result_preview || step.thought || step.error);
    return (
        <li className="text-xs">
            <button
                type="button"
                className={`flex w-full items-center gap-2 text-left px-2 py-1 rounded hover:bg-muted/60 ${step.error ? 'text-red-600 dark:text-red-400' : ''}`}
                onClick={() => expandable && setOpen((value) => !value)}
            >
                {expandable ? open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" /> : <span className="w-3" />}
                <span className="tabular-nums text-muted-foreground w-6">{index + 1}</span>
                <span className="font-mono truncate">{label}</span>
                {step.duration_ms != null && <span className="ml-auto text-muted-foreground">{Math.round(step.duration_ms)} ms</span>}
            </button>
            {open && (
                <div className="ml-11 mb-2 space-y-1">
                    {step.thought && <p className="whitespace-pre-wrap text-muted-foreground">{step.thought}</p>}
                    {step.args && (
                        <pre className="max-h-40 overflow-auto rounded bg-muted p-2 font-mono text-[11px] whitespace-pre-wrap break-all">{step.args}</pre>
                    )}
                    {step.result_preview && (
                        <pre className="max-h-40 overflow-auto rounded bg-muted p-2 font-mono text-[11px] whitespace-pre-wrap break-all">
                            {step.result_preview}
                        </pre>
                    )}
                    {step.error && <p className="text-red-600 dark:text-red-400">{step.error}</p>}
                </div>
            )}
        </li>
    );
};

const WakeDetail: React.FC<{ jobId: string; wake: Wake; livePlan?: WakePlan | null }> = ({ jobId, wake, livePlan }) => {
    const { get, post } = useApi();
    const [detail, setDetail] = useState<Wake | null>(null);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);
    const active = ACTIVE_WAKE_STATUSES.has(wake.status);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        get(`/jobs/${jobId}/wakes/${wake.id}`)
            .then((response) => {
                if (!cancelled) setDetail(response.data);
            })
            .catch((error) => toast.error(apiErrorMessage(error, 'Could not load the wake')))
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // Reload when the wake reaches a terminal status so steps appear.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, wake.id, wake.status]);

    const cancel = async () => {
        setCancelling(true);
        try {
            await post(`/jobs/${jobId}/wakes/${wake.id}/cancel`, {});
            toast.success('Cancellation requested');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not cancel the wake'));
        } finally {
            setCancelling(false);
        }
    };

    const plan = (active && livePlan) || detail?.plan || wake.plan;
    const steps = detail?.steps || [];

    return (
        <div className="mt-2 ml-7 space-y-3 text-sm">
            {wake.error?.message && <p className="text-red-600 dark:text-red-400 text-xs">{wake.error.message}</p>}
            {plan?.items?.length ? (
                <div>
                    <div className="text-xs text-muted-foreground mb-1">Plan</div>
                    <WakePlanList plan={plan} />
                </div>
            ) : null}
            {loading ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading steps…
                </div>
            ) : steps.length ? (
                <div>
                    <div className="text-xs text-muted-foreground mb-1">
                        Steps ({steps.length})
                    </div>
                    <ol className="rounded border divide-y">
                        {steps.map((step, index) => (
                            <StepRow key={index} step={step} index={index} />
                        ))}
                    </ol>
                </div>
            ) : (
                <p className="text-xs text-muted-foreground">{active ? 'Working… steps appear when the wake finishes.' : 'No recorded steps.'}</p>
            )}
            {active && (
                <Button variant="outline" size="sm" onClick={cancel} disabled={cancelling}>
                    {cancelling ? 'Cancelling…' : 'Cancel this wake'}
                </Button>
            )}
        </div>
    );
};

/** Wakes (one working session each), newest first, expandable to plan + steps. */
export const JobWakes: React.FC<{
    jobId: string;
    wakes: Wake[];
    activeWakeId?: string | null;
    livePlan?: WakePlan | null;
    focusWakeId?: string | null;
}> = ({ jobId, wakes, activeWakeId, livePlan, focusWakeId }) => {
    const [openId, setOpenId] = useState<string | null>(null);
    useEffect(() => {
        if (focusWakeId) setOpenId(focusWakeId);
    }, [focusWakeId]);

    return (
        <div>
            <h3 className="text-lg font-semibold mb-3">Wakes</h3>
            {wakes.length ? (
                <div className="rounded-lg border divide-y">
                    {wakes.map((wake) => {
                        const open = openId === wake.id;
                        const isActive = wake.id === activeWakeId || ACTIVE_WAKE_STATUSES.has(wake.status);
                        return (
                            <div key={wake.id} className="px-3 py-2">
                                <button type="button" className="flex w-full items-start gap-2 text-left" onClick={() => setOpenId(open ? null : wake.id)}>
                                    {open ? <ChevronDown className="h-4 w-4 mt-0.5 shrink-0" /> : <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />}
                                    <div className="min-w-0 grow">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <ServiceStatusBadge status={isActive && wake.status === 'pending' ? 'queued' : wake.status} />
                                            <span className="text-sm font-medium">{wakeReasonLabel(wake.reason)}</span>
                                            {wake.mode && wake.mode !== 'job' && (
                                                <span className="text-xs text-muted-foreground">· {wake.mode} routine</span>
                                            )}
                                            <span className="text-xs text-muted-foreground" title={wake.created_at ? formatDate(wake.created_at) : undefined}>
                                                {formatRelativeTime(wake.started_at || wake.queued_at || wake.created_at)}
                                            </span>
                                        </div>
                                        <div className="text-sm text-muted-foreground truncate mt-0.5">
                                            {wake.summary || wake.error?.message || (isActive ? 'Working…' : wake.stop_reason || '—')}
                                        </div>
                                    </div>
                                    <div className="text-right text-xs text-muted-foreground shrink-0 tabular-nums">
                                        <div>{formatCredits(wake.credits)} credits</div>
                                        <div>{formatDuration(wake.started_at, wake.completed_at)}</div>
                                    </div>
                                </button>
                                {open && <WakeDetail jobId={jobId} wake={wake} livePlan={wake.id === activeWakeId ? livePlan : null} />}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <p className="text-sm text-muted-foreground">No wakes yet.</p>
            )}
        </div>
    );
};

export default JobWakes;
